/**
 * "Copy → Screen": copies every flipbook page's contents onto `screen`,
 * scaled to fit and centred, grouped as one object, and animated by the
 * generated flipbookPlay clip so the screen plays the flipbook inline.
 *
 * Re-baking the same screen replaces the previous bake (matched by
 * customData.flipbookBake.screenId) rather than stacking a second copy.
 */
import { arrayToMap, randomId } from "@excalidraw/common";
import { duplicateElements, syncInvalidIndices } from "@excalidraw/element";
import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { pointFrom } from "@excalidraw/math";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/element/types";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import type { GlobalPoint } from "@excalidraw/math";

import { makeClip } from "../animation/clips";
import {
  getElementAnimations,
  withElementAnimations,
} from "../animation/types";

import {
  FLIPBOOK_BAKE_KEY,
  computeBakeLayout,
  findBakedIds,
  getBakeTag,
  planScale,
  screenCycleMs,
  unionBox,
} from "./bake";
import { LEGACY_BOOK_ID, getFlipbookPages } from "./ops";

/** Fraction of the screen's smaller side left as breathing room. */
const BAKE_PADDING_RATIO = 0.04;

export const applyBake = (
  api: ExcalidrawImperativeAPI,
  screen: ExcalidrawFrameLikeElement,
  fps: number,
  bookId?: string,
): { pageCount: number } | null => {
  const scene = api.getSceneElements();
  const book = bookId ?? LEGACY_BOOK_ID;
  const pages = getFlipbookPages(scene, book);
  if (pages.length === 0) {
    return null;
  }

  // Membership is ownership — `el.frameId === page.id`. FlipbookBar's playback
  // uses getElementsOverlappingFrame instead, which also catches elements
  // merely sitting on top of a page; a bake copies what a page owns.
  // pageIndex is recovered later via origIdToDuplicateId, never from geometry.
  const pageIndexByOrigId = new Map<string, number>();
  const members: ExcalidrawElement[] = [];
  pages.forEach((page, pageIndex) => {
    for (const el of scene) {
      if (el.frameId === page.id) {
        pageIndexByOrigId.set(el.id, pageIndex);
        members.push(el);
      }
    }
  });
  if (members.length === 0) {
    return null;
  }

  // one duplicate pass over the whole scene (never the page frames —
  // frames cannot nest, and only members are in idsOfElementsToDuplicate)
  const { duplicatedElements, elementsWithDuplicates, origIdToDuplicateId } =
    duplicateElements({
      type: "in-place",
      elements: scene,
      idsOfElementsToDuplicate: arrayToMap(members),
      appState: { editingGroupId: null, selectedGroupIds: {} },
      randomizeSeed: false,
    });

  const pageIndexByCopyId = new Map<string, number>();
  for (const [origId, copyId] of origIdToDuplicateId) {
    const pageIndex = pageIndexByOrigId.get(origId);
    if (pageIndex !== undefined) {
      pageIndexByCopyId.set(copyId, pageIndex);
    }
  }
  const copies = duplicatedElements.filter((el) =>
    pageIndexByCopyId.has(el.id),
  );
  if (copies.length === 0) {
    return null;
  }

  // Every page maps onto the screen the same way, so the pages *overlay*
  // rather than tiling across it: the fit comes from the page frames' own box
  // (pages are laid out side by side on the canvas, so a union of the copies'
  // absolute bounds would shrink the whole strip into the screen), and each
  // copy is scaled about its own page's origin.
  // ponytail: pages of differing size anchor at the union's top-left rather
  // than centring individually — pages are the same size in practice.
  const content = unionBox(
    pages.map((p) => ({ x: 0, y: 0, width: p.width, height: p.height })),
  );
  if (!content) {
    return null;
  }
  const padding = Math.min(screen.width, screen.height) * BAKE_PADDING_RATIO;
  const layout = computeBakeLayout(content, screen, padding);
  const target = pointFrom<GlobalPoint>(layout.x, layout.y);
  const origins = pages.map((p) => pointFrom<GlobalPoint>(p.x, p.y));

  // tag, group, reparent, scale and animate in one mutation per copy.
  // fps is clamped here, not trusted: the panel field only clamps on blur, so
  // a click that fires before the blur's state flush can hand us a raw value
  // (e.g. 0 → Infinity duration → a bake that never animates).
  const safeFps = Math.min(30, Math.max(1, Math.round(fps) || 1));
  const groupId = randomId();
  const totalMs = pages.length * (1000 / safeFps);
  // ids the re-bake replaces — this book's previous copies on this screen only,
  // so a second flipbook baked onto the same screen survives untouched
  const stale = findBakedIds(scene, screen.id, book, LEGACY_BOOK_ID);
  // hold the screen for one full cycle of the LONGEST flipbook on it. Without
  // a duration the screen keeps DEFAULT_SCREEN_DURATION_MS (2000ms) and a
  // Settings "export screens" pads any sub-2s flipbook with blank trailing
  // frames (recordScreens samples past the cycle clamp onto the preset's
  // trailing opacity:0 set); with two books, the shorter one loops instead of
  // cutting the longer one off.
  const survivingBakes = scene.filter((el) => !stale.has(el.id));
  const slotMs = 1000 / safeFps;
  // every book on this screen plays for the same window; a book whose own
  // cycle is shorter tiles inside it (flipbookPlay's slotMs param)
  const windowMs = screenCycleMs(survivingBakes, screen.id, totalMs);
  api.mutateElement(screen, {
    customData: {
      ...(screen.customData ?? {}),
      screenDurationMs: windowMs,
    },
  });
  for (const el of copies) {
    const pageIndex = pageIndexByCopyId.get(el.id)!;
    api.mutateElement(el, {
      ...planScale(el, layout.scale, origins[pageIndex], target),
      frameId: screen.id,
      groupIds: [...el.groupIds, groupId],
      // Every page but the first is hidden in the scene, so the screen shows
      // page 1 alone at rest instead of the whole book stacked on top of
      // itself. The flipbookPlay clip sets `revealHidden`, which brings them
      // back for playback and export (see staticScene.ts's hidden filter).
      hidden: pageIndex > 0,
      customData: {
        ...withElementAnimations(el, [
          makeClip("flipbookPlay", {
            duration: windowMs,
            repeat: -1,
            params: { pageIndex, pageCount: pages.length, slotMs },
          }),
        ]),
        [FLIPBOOK_BAKE_KEY]: {
          screenId: screen.id,
          pageIndex,
          bookId: book,
          cycleMs: totalMs,
        },
      },
    });
  }

  // a longer book can widen the screen's window; stretch the books already
  // baked here to match, so they keep tiling for the whole of it instead of
  // freezing on their last page
  for (const el of survivingBakes) {
    const tag = getBakeTag(el);
    if (!tag || tag.screenId !== screen.id) {
      continue;
    }
    const clip = getElementAnimations(el)[0];
    if (!clip || clip.preset !== "flipbookPlay" || clip.duration === windowMs) {
      continue;
    }
    api.mutateElement(el, {
      customData: withElementAnimations(el, [{ ...clip, duration: windowMs }]),
    });
  }

  // commit: the duplicated scene, minus this book's previous bake, with the
  // fresh copies lifted to just before the screen frame — frame children sit
  // below their frame in z-order, so that slot is the top of the screen and
  // the flipbook plays above whatever else the screen holds.
  // in-place clones inherit their originals' fractional indices — sync so
  // the strictly-increasing invariant holds (same as FlipbookBar.tsx:273)
  const copyIds = new Set(copies.map((el) => el.id));
  const kept = elementsWithDuplicates.filter(
    (el) => !stale.has(el.id) && !copyIds.has(el.id),
  );
  const screenAt = kept.findIndex((el) => el.id === screen.id);
  const insertAt = screenAt === -1 ? kept.length : screenAt;
  api.updateScene({
    elements: syncInvalidIndices([
      ...kept.slice(0, insertAt),
      ...copies,
      ...kept.slice(insertAt),
    ]),
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });

  return { pageCount: pages.length };
};
